"use client";

import { c as _c } from "react/compiler-runtime";
import { Suspense } from "react";
import { useSearchParams } from "next/navigation";
import { Link } from "@/components/LocaleLink";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Footer } from "@/components/Footer";
import { useAuthStore } from "@/lib/store";
import { useTranslation } from "@/lib/hooks/useTranslation";
import { ArrowRight, BellRing, Check, CheckCircle2, ChevronDown, FileText, Lock, MousePointerClick, Sparkles, Target, Wand2, Zap } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

// Set NEXT_PUBLIC_EXTENSION_STORE_URL once the listing is live. Until then we
// surface a "coming soon" waitlist CTA rather than pushing users to a generic
// Chrome search that would feel broken.
const CHROME_STORE_URL = process.env.NEXT_PUBLIC_EXTENSION_STORE_URL ?? null;
const EXTENSION_PUBLISHED = CHROME_STORE_URL !== null;
const WAITLIST_HREF = "/register?ref=extension-waitlist";
const SUPPORTED_SITES = ["LinkedIn", "Indeed", "Welcome to the Jungle", "APEC", "France Travail", "Glassdoor"];
function InstalledBanner(t0) {
  const $ = _c(12);
  const {
    isFr,
    isAuthed
  } = t0;
  const searchParams = useSearchParams();
  if (searchParams.get("installed") !== "1") {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = isFr ? "Extension install\xE9e ! " : "Extension installed! ";
  let t3;
  if ($[1] !== t2) {
    t3 = <b>{t2}</b>;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = isAuthed ? isFr ? "Ouvre une offre sur LinkedIn ou Indeed puis clique sur l'ic\xF4ne Velyq pour la clipper." : "Open a job on LinkedIn or Indeed, then click the Velyq icon to clip it." : isFr ? "Connecte-toi pour synchroniser tes offres avec le tracker." : "Sign in to sync your offers with the tracker.";
  let t5;
  if ($[3] !== t3 || $[4] !== t4) {
    t5 = <div className="flex items-start gap-2 text-sm">{t1}<p>{t3}{t4}</p></div>;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== isAuthed || $[7] !== isFr) {
    t6 = !isAuthed && <Link href="/login"><Button size="sm" variant="default" className="gap-1.5">{isFr ? "Me connecter" : "Sign in"}<ArrowRight className="h-3.5 w-3.5" /></Button></Link>;
    $[6] = isAuthed;
    $[7] = isFr;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = <div className="border-b border-primary/30 bg-primary/10"><div className="container mx-auto flex flex-wrap items-center justify-between gap-3 px-4 py-3">{t5}{t6}</div></div>;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
}
export default function ExtensionPage() {
  const $ = _c(205);
  const {
    accessToken
  } = useAuthStore();
  const {
    locale
  } = useTranslation();
  const isFr = locale === "fr";
  const isAuthed = !!accessToken;
  let t0;
  if ($[0] !== isAuthed || $[1] !== isFr) {
    t0 = <Suspense fallback={null}><InstalledBanner isFr={isFr} isAuthed={isAuthed} /></Suspense>;
    $[0] = isAuthed;
    $[1] = isFr;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Link href="/"><Logo /></Link>;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <LanguageSwitcher />;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== isAuthed || $[6] !== isFr) {
    t3 = <header className="sticky top-0 z-40 border-b bg-background/90 backdrop-blur"><div className="container mx-auto flex h-16 items-center justify-between px-4">{t1}<div className="flex items-center gap-3">{t2}{isAuthed ? <Link href="/dashboard"><Button variant="outline" size="sm">Dashboard</Button></Link> : <><Link href="/login"><Button variant="ghost" size="sm">{isFr ? "Connexion" : "Log in"}</Button></Link><Link href="/register"><Button size="sm">{isFr ? "S'inscrire" : "Sign up"}</Button></Link></>}</div></div></header>;
    $[5] = isAuthed;
    $[6] = isFr;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Sparkles className="h-3 w-3" />;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = isFr ? "Nouveau" : "New";
  let t6;
  if ($[9] !== t5) {
    t6 = <Badge variant="outline" className="mb-5 gap-1.5 border-primary/40 bg-primary/10 text-primary">{t4}{t5} · Chrome, Edge, Brave</Badge>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = isFr ? "Sauvegarde une offre en " : "Save a job in ";
  const t8 = isFr ? "un clic" : "one click";
  let t9;
  if ($[11] !== t8) {
    t9 = <span className="text-primary">{t8}</span>;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <br />;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const t11 = isFr ? "Pr\xE9pare ta candidature pendant que tu lis." : "Your application is ready before you finish reading.";
  let t12;
  if ($[14] !== t11 || $[15] !== t7 || $[16] !== t9) {
    t12 = <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl lg:text-6xl">{t7}{t9}.{t10}{t11}</h1>;
    $[14] = t11;
    $[15] = t7;
    $[16] = t9;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const t13 = isFr ? "L'extension Chrome Velyq clippe n'importe quelle offre LinkedIn, Indeed, WTTJ, APEC ou France Travail dans ton tracker. En parall\xE8le elle g\xE9n\xE8re ton score ATS, un brouillon de lettre de motivation, et te rappelle chaque relance." : "The Velyq Chrome extension clips any LinkedIn, Indeed, WTTJ, APEC or France Travail offer to your tracker. It generates your ATS score, a tailored cover-letter draft, and nudges you before every follow-up.";
  let t14;
  if ($[18] !== t13) {
    t14 = <p className="mt-5 max-w-2xl text-base text-muted-foreground md:text-lg">{t13}</p>;
    $[18] = t13;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== isFr) {
    t15 = EXTENSION_PUBLISHED ? <a href={CHROME_STORE_URL ?? "#"} target="_blank" rel="noreferrer"><Button size="lg" className="gap-2 px-6"><Zap className="h-4 w-4" />{isFr ? "Ajouter \xE0 Chrome \u2014 gratuit" : "Add to Chrome \u2014 free"}</Button></a> : <Link href={WAITLIST_HREF}><Button size="lg" className="gap-2 px-6"><BellRing className="h-4 w-4" />{isFr ? "Rejoindre la liste b\xEAta" : "Join the beta waitlist"}</Button></Link>;
    $[20] = isFr;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  const t16 = isFr ? "Voir comment \xE7a marche" : "See how it works";
  let t17;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <ChevronDown className="h-4 w-4" />;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] !== t16) {
    t18 = <a href="#how-it-works"><Button size="lg" variant="outline" className="gap-2">{t16}{t17}</Button></a>;
    $[23] = t16;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== t15 || $[26] !== t18) {
    t19 = <div className="mt-7 flex flex-col items-center gap-3 sm:flex-row">{t15}{t18}</div>;
    $[25] = t15;
    $[26] = t18;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  const t20 = EXTENSION_PUBLISHED ? isFr ? "Installation en 15 secondes \xB7 Aucune donn\xE9e vendue \xB7 RGPD" : "15-second install \xB7 No data sold \xB7 GDPR-compliant" : isFr ? "Sortie imminente \xB7 On te pr\xE9vient d\xE8s qu'elle est en ligne" : "Launching soon \xB7 We'll ping you the moment it ships";
  let t21;
  if ($[28] !== t20) {
    t21 = <p className="mt-4 text-xs text-muted-foreground">{t20}</p>;
    $[28] = t20;
    $[29] = t21;
  } else {
    t21 = $[29];
  }
  let t22;
  if ($[30] !== t12 || $[31] !== t14 || $[32] !== t19 || $[33] !== t21 || $[34] !== t6) {
    t22 = <div className="mx-auto flex max-w-4xl flex-col items-center text-center">{t6}{t12}{t14}{t19}{t21}</div>;
    $[30] = t12;
    $[31] = t14;
    $[32] = t19;
    $[33] = t21;
    $[34] = t6;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  const t23 = isFr ? "Sites support\xE9s d\xE8s l'installation" : "Supported out of the box";
  let t24;
  if ($[36] !== t23) {
    t24 = <p className="mb-3 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t23}</p>;
    $[36] = t23;
    $[37] = t24;
  } else {
    t24 = $[37];
  }
  let t25;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = SUPPORTED_SITES.map(_temp);
    $[38] = t25;
  } else {
    t25 = $[38];
  }
  const t26 = isFr ? "+ fallback texte brut sur n'importe quelle page" : "+ raw-text fallback on any page";
  let t27;
  if ($[39] !== t26) {
    t27 = <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">{t25}<span className="text-sm text-muted-foreground">{t26}</span></div>;
    $[39] = t26;
    $[40] = t27;
  } else {
    t27 = $[40];
  }
  let t28;
  if ($[41] !== t24 || $[42] !== t27) {
    t28 = <div className="mx-auto mt-12 max-w-4xl rounded-xl border bg-background/60 p-4 backdrop-blur">{t24}{t27}</div>;
    $[41] = t24;
    $[42] = t27;
    $[43] = t28;
  } else {
    t28 = $[43];
  }
  let t29;
  if ($[44] !== t22 || $[45] !== t28) {
    t29 = <section className="container mx-auto px-4 pb-12 pt-16 md:pt-24">{t22}{t28}</section>;
    $[44] = t22;
    $[45] = t28;
    $[46] = t29;
  } else {
    t29 = $[46];
  }
  const t30 = isFr ? "3 \xE9tapes, z\xE9ro friction" : "3 steps, zero friction";
  let t31;
  if ($[47] !== t30) {
    t31 = <h2 className="font-display text-3xl font-bold tracking-tight md:text-4xl">{t30}</h2>;
    $[47] = t30;
    $[48] = t31;
  } else {
    t31 = $[48];
  }
  const t32 = isFr ? "Rien \xE0 copier-coller, rien \xE0 retaper. L'extension fait le travail pendant que tu gardes ton focus sur la bonne offre." : "Nothing to copy-paste. The extension does the work while you focus on finding the right role.";
  let t33;
  if ($[49] !== t32) {
    t33 = <p className="mt-3 text-muted-foreground">{t32}</p>;
    $[49] = t32;
    $[50] = t33;
  } else {
    t33 = $[50];
  }
  let t34;
  if ($[51] !== t31 || $[52] !== t33) {
    t34 = <div className="mx-auto mb-12 max-w-2xl text-center">{t31}{t33}</div>;
    $[51] = t31;
    $[52] = t33;
    $[53] = t34;
  } else {
    t34 = $[53];
  }
  let t35;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <MousePointerClick className="h-5 w-5" />;
    $[54] = t35;
  } else {
    t35 = $[54];
  }
  const t36 = isFr ? "Ouvre une offre" : "Open a job";
  const t37 = isFr ? "LinkedIn, Indeed, WTTJ, APEC\u2026 Velyq d\xE9tecte automatiquement l'offre et en extrait le titre, l'entreprise, le lieu, la description." : "LinkedIn, Indeed, WTTJ, APEC\u2026 Velyq auto-detects the offer and pulls title, company, location, description.";
  let t38;
  if ($[55] !== t36 || $[56] !== t37) {
    t38 = <StepCard index={1} icon={t35} title={t36} body={t37} />;
    $[55] = t36;
    $[56] = t37;
    $[57] = t38;
  } else {
    t38 = $[57];
  }
  let t39;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <Sparkles className="h-5 w-5" />;
    $[58] = t39;
  } else {
    t39 = $[58];
  }
  const t40 = isFr ? "Clique \xAB Ajouter \xE0 Velyq \xBB" : "Click \"Add to Velyq\"";
  const t41 = isFr ? "L'offre atterrit dans ton tracker. En parall\xE8le, l'IA analyse le match avec ton CV et r\xE9dige un brouillon de lettre taill\xE9e pour ce poste." : "The offer lands in your tracker. In parallel, AI scores the match with your CV and drafts a tailored cover letter.";
  let t42;
  if ($[59] !== t40 || $[60] !== t41) {
    t42 = <StepCard index={2} icon={t39} title={t40} body={t41} />;
    $[59] = t40;
    $[60] = t41;
    $[61] = t42;
  } else {
    t42 = $[61];
  }
  let t43;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <BellRing className="h-5 w-5" />;
    $[62] = t43;
  } else {
    t43 = $[62];
  }
  const t44 = isFr ? "Re\xE7ois les rappels" : "Get the nudges";
  const t45 = isFr ? "Deadline dans 48h ? Relance pr\xE9vue demain ? L'extension te ping via une notification Chrome. Tu ne laisses plus aucune candidature dormir." : "Deadline in 48h? Follow-up due tomorrow? The extension pings you via a Chrome notification. No more forgotten applications.";
  let t46;
  if ($[63] !== t44 || $[64] !== t45) {
    t46 = <StepCard index={3} icon={t43} title={t44} body={t45} />;
    $[63] = t44;
    $[64] = t45;
    $[65] = t46;
  } else {
    t46 = $[65];
  }
  let t47;
  if ($[66] !== t38 || $[67] !== t42 || $[68] !== t46) {
    t47 = <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-3">{t38}{t42}{t46}</div>;
    $[66] = t38;
    $[67] = t42;
    $[68] = t46;
    $[69] = t47;
  } else {
    t47 = $[69];
  }
  let t48;
  if ($[70] !== t34 || $[71] !== t47) {
    t48 = <section id="how-it-works" className="container mx-auto px-4 py-16">{t34}{t47}</section>;
    $[70] = t34;
    $[71] = t47;
    $[72] = t48;
  } else {
    t48 = $[72];
  }
  const t49 = isFr ? "Tout ce qu'il te faut dans un seul outil" : "Everything you need in one place";
  let t50;
  if ($[73] !== t49) {
    t50 = <h2 className="font-display text-3xl font-bold tracking-tight md:text-4xl">{t49}</h2>;
    $[73] = t49;
    $[74] = t50;
  } else {
    t50 = $[74];
  }
  const t51 = isFr ? "Pas une extension qui liste des offres \u2014 un pipeline qui te porte de la d\xE9couverte \xE0 la signature." : "Not another job list \u2014 a pipeline that carries you from discovery to signed offer.";
  let t52;
  if ($[75] !== t51) {
    t52 = <p className="mt-3 text-muted-foreground">{t51}</p>;
    $[75] = t51;
    $[76] = t52;
  } else {
    t52 = $[76];
  }
  let t53;
  if ($[77] !== t50 || $[78] !== t52) {
    t53 = <div className="mx-auto mb-12 max-w-2xl text-center">{t50}{t52}</div>;
    $[77] = t50;
    $[78] = t52;
    $[79] = t53;
  } else {
    t53 = $[79];
  }
  let t54;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <Sparkles className="h-5 w-5" />;
    $[80] = t54;
  } else {
    t54 = $[80];
  }
  const t55 = isFr ? "Clipping 1 clic" : "One-click clipping";
  const t56 = isFr ? "D\xE9tection site-aware + fallback texte brut. L'offre est pars\xE9e, d\xE9dupliqu\xE9e, puis vers\xE9e dans le Kanban." : "Site-aware detection + raw-text fallback. Offers are parsed, deduped, dropped into the Kanban.";
  const t57 = isFr ? "Gratuit" : "Free";
  let t58;
  if ($[81] !== t55 || $[82] !== t56 || $[83] !== t57) {
    t58 = <FeatureCard icon={t54} title={t55} body={t56} badge={t57} />;
    $[81] = t55;
    $[82] = t56;
    $[83] = t57;
    $[84] = t58;
  } else {
    t58 = $[84];
  }
  let t59;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = <Target className="h-5 w-5" />;
    $[85] = t59;
  } else {
    t59 = $[85];
  }
  const t60 = isFr ? "Score ATS instantan\xE9" : "Instant ATS score";
  const t61 = isFr ? "D\xE8s le clip, Velyq compare ton CV \xE0 l'offre et te donne un score sur 100 + les mots-cl\xE9s manquants." : "On clip, Velyq scores your CV against the offer out of 100 and flags missing keywords.";
  const t62 = isFr ? "Gratuit" : "Free";
  let t63;
  if ($[86] !== t60 || $[87] !== t61 || $[88] !== t62) {
    t63 = <FeatureCard icon={t59} title={t60} body={t61} badge={t62} />;
    $[86] = t60;
    $[87] = t61;
    $[88] = t62;
    $[89] = t63;
  } else {
    t63 = $[89];
  }
  let t64;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = <FileText className="h-5 w-5" />;
    $[90] = t64;
  } else {
    t64 = $[90];
  }
  const t65 = isFr ? "Lettre de motivation IA" : "AI cover letter";
  const t66 = isFr ? "Brouillon tailored g\xE9n\xE9r\xE9 en parall\xE8le, puis ouvrable en 1 clic dans l'\xE9diteur Velyq pour les ajustements." : "A tailored draft is generated in parallel, openable in the Velyq editor for quick tweaks.";
  const t67 = isFr ? "Gratuit" : "Free";
  let t68;
  if ($[91] !== t65 || $[92] !== t66 || $[93] !== t67) {
    t68 = <FeatureCard icon={t64} title={t65} body={t66} badge={t67} />;
    $[91] = t65;
    $[92] = t66;
    $[93] = t67;
    $[94] = t68;
  } else {
    t68 = $[94];
  }
  let t69;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t69 = <Wand2 className="h-5 w-5" />;
    $[95] = t69;
  } else {
    t69 = $[95];
  }
  const t70 = isFr ? "Autofill des formulaires" : "Form autofill";
  const t71 = isFr ? "Nom, email, LinkedIn, CV upload\xE9, lettre inject\xE9e. Optimis\xE9 pour Greenhouse, Lever, Workday, SmartRecruiters." : "Name, email, LinkedIn, CV upload, letter inject. Tuned for Greenhouse, Lever, Workday, SmartRecruiters.";
  let t72;
  if ($[96] !== t70 || $[97] !== t71) {
    t72 = <FeatureCard icon={t69} title={t70} body={t71} badge="Pro" />;
    $[96] = t70;
    $[97] = t71;
    $[98] = t72;
  } else {
    t72 = $[98];
  }
  let t73;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t73 = <BellRing className="h-5 w-5" />;
    $[99] = t73;
  } else {
    t73 = $[99];
  }
  const t74 = isFr ? "Rappels de relance" : "Follow-up reminders";
  const t75 = isFr ? "Notification Chrome avant chaque next-step. Badge sur l'ic\xF4ne avec le nombre de candidatures actives." : "Chrome notification before each next step. Toolbar badge with your active count.";
  const t76 = isFr ? "Gratuit" : "Free";
  let t77;
  if ($[100] !== t74 || $[101] !== t75 || $[102] !== t76) {
    t77 = <FeatureCard icon={t73} title={t74} body={t75} badge={t76} />;
    $[100] = t74;
    $[101] = t75;
    $[102] = t76;
    $[103] = t77;
  } else {
    t77 = $[103];
  }
  let t78;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t78 = <Lock className="h-5 w-5" />;
    $[104] = t78;
  } else {
    t78 = $[104];
  }
  const t79 = isFr ? "Sync s\xE9curis\xE9e avec Velyq" : "Secure sync with Velyq";
  const t80 = isFr ? "Auth OAuth explicite, tokens chiffr\xE9s stock\xE9s localement, aucun scraping auto, aucune donn\xE9e revendue." : "Explicit OAuth consent, encrypted tokens stored locally, no auto-scraping, no data resold.";
  const t81 = isFr ? "Gratuit" : "Free";
  let t82;
  if ($[105] !== t79 || $[106] !== t80 || $[107] !== t81) {
    t82 = <FeatureCard icon={t78} title={t79} body={t80} badge={t81} />;
    $[105] = t79;
    $[106] = t80;
    $[107] = t81;
    $[108] = t82;
  } else {
    t82 = $[108];
  }
  let t83;
  if ($[109] !== t58 || $[110] !== t63 || $[111] !== t68 || $[112] !== t72 || $[113] !== t77 || $[114] !== t82) {
    t83 = <div className="mx-auto grid max-w-6xl gap-4 md:grid-cols-2 lg:grid-cols-3">{t58}{t63}{t68}{t72}{t77}{t82}</div>;
    $[109] = t58;
    $[110] = t63;
    $[111] = t68;
    $[112] = t72;
    $[113] = t77;
    $[114] = t82;
    $[115] = t83;
  } else {
    t83 = $[115];
  }
  let t84;
  if ($[116] !== t53 || $[117] !== t83) {
    t84 = <section className="bg-muted/40 py-16"><div className="container mx-auto px-4">{t53}{t83}</div></section>;
    $[116] = t53;
    $[117] = t83;
    $[118] = t84;
  } else {
    t84 = $[118];
  }
  const t85 = isFr ? "Tout ce qu'il faut pour clipper, suivre, relancer" : "Everything to clip, track and follow up";
  let t86;
  if ($[119] !== isFr) {
    t86 = isFr ? ["Clipping illimit\xE9", "Score ATS + lettre de motivation", "Tracker Kanban, liste, calendrier", "Rappels de relance"] : ["Unlimited clipping", "ATS score + cover letter", "Kanban / list / calendar tracker", "Follow-up reminders"];
    $[119] = isFr;
    $[120] = t86;
  } else {
    t86 = $[120];
  }
  const t87 = EXTENSION_PUBLISHED ? isFr ? "Installer gratuitement" : "Install for free" : isFr ? "Rejoindre la b\xEAta" : "Join the beta";
  let t88;
  if ($[121] !== t85 || $[122] !== t86 || $[123] !== t87) {
    t88 = <PlanCard tier="Free" tagline={t85} features={t86} ctaLabel={t87} ctaHref={EXTENSION_PUBLISHED ? CHROME_STORE_URL as string : WAITLIST_HREF} highlight={false} />;
    $[121] = t85;
    $[122] = t86;
    $[123] = t87;
    $[124] = t88;
  } else {
    t88 = $[124];
  }
  const t89 = isFr ? "Pour les chercheurs actifs qui postulent en vrai" : "For active candidates who actually apply";
  let t90;
  if ($[125] !== isFr) {
    t90 = isFr ? ["Tout le gratuit +", "Autofill des formulaires de candidature", "Upload CV automatique", "Lettre de motivation inject\xE9e", "Overrides Greenhouse / Lever / Workday"] : ["Everything in Free +", "Auto-fill application forms", "Auto-upload CV", "Cover letter injection", "Greenhouse / Lever / Workday overrides"];
    $[125] = isFr;
    $[126] = t90;
  } else {
    t90 = $[126];
  }
  const t91 = isFr ? "D\xE9couvrir Pro" : "Explore Pro";
  let t92;
  if ($[127] !== t89 || $[128] !== t90 || $[129] !== t91) {
    t92 = <PlanCard tier="Pro" tagline={t89} features={t90} ctaLabel={t91} ctaHref="/pricing" highlight={true} />;
    $[127] = t89;
    $[128] = t90;
    $[129] = t91;
    $[130] = t92;
  } else {
    t92 = $[130];
  }
  let t93;
  if ($[131] !== t88 || $[132] !== t92) {
    t93 = <section className="container mx-auto px-4 py-16"><div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-2">{t88}{t92}</div></section>;
    $[131] = t88;
    $[132] = t92;
    $[133] = t93;
  } else {
    t93 = $[133];
  }
  const t94 = isFr ? "Questions fr\xE9quentes" : "Frequently asked";
  let t95;
  if ($[134] !== t94) {
    t95 = <h2 className="mb-10 text-center font-display text-3xl font-bold tracking-tight md:text-4xl">{t94}</h2>;
    $[134] = t94;
    $[135] = t95;
  } else {
    t95 = $[135];
  }
  const t96 = isFr ? "Est-ce que l'extension scrape LinkedIn automatiquement ?" : "Does the extension auto-scrape LinkedIn?";
  let t97;
  if ($[136] !== t96) {
    t97 = <AccordionTrigger className="text-left">{t96}</AccordionTrigger>;
    $[136] = t96;
    $[137] = t97;
  } else {
    t97 = $[137];
  }
  const t98 = isFr ? "Non. On ne sauvegarde que ce que tu cliques, sur la page que tu regardes. Aucun crawl en arri\xE8re-plan, aucune collecte de profils recruteurs, aucune violation des TOS des plateformes." : "No. We only save what you click, on the page you're viewing. No background crawl, no recruiter-profile harvesting, no TOS violations.";
  let t99;
  if ($[138] !== t98) {
    t99 = <AccordionContent>{t98}</AccordionContent>;
    $[138] = t98;
    $[139] = t99;
  } else {
    t99 = $[139];
  }
  let t100;
  if ($[140] !== t97 || $[141] !== t99) {
    t100 = <AccordionItem value="q1">{t97}{t99}</AccordionItem>;
    $[140] = t97;
    $[141] = t99;
    $[142] = t100;
  } else {
    t100 = $[142];
  }
  const t101 = isFr ? "Quelles donn\xE9es Velyq stocke quand je clippe une offre ?" : "What data does Velyq store when I clip?";
  let t102;
  if ($[143] !== t101) {
    t102 = <AccordionTrigger className="text-left">{t101}</AccordionTrigger>;
    $[143] = t101;
    $[144] = t102;
  } else {
    t102 = $[144];
  }
  const t103 = isFr ? "Le titre, l'entreprise, le lieu, la description et l'URL de l'offre. Li\xE9 \xE0 ton compte Velyq existant. Rien n'est partag\xE9 avec des tiers. D\xE9tails complets dans notre " : "Title, company, location, description and URL of the offer. Linked to your Velyq account. Nothing shared with third parties. Full details in our ";
  const t104 = isFr ? "politique d\xE9di\xE9e" : "dedicated policy";
  let t105;
  if ($[145] !== t104) {
    t105 = <Link href="/privacy/extension" className="text-primary underline">{t104}</Link>;
    $[145] = t104;
    $[146] = t105;
  } else {
    t105 = $[146];
  }
  let t106;
  if ($[147] !== t103 || $[148] !== t105) {
    t106 = <AccordionContent>{t103}{t105}.</AccordionContent>;
    $[147] = t103;
    $[148] = t105;
    $[149] = t106;
  } else {
    t106 = $[149];
  }
  let t107;
  if ($[150] !== t102 || $[151] !== t106) {
    t107 = <AccordionItem value="q2">{t102}{t106}</AccordionItem>;
    $[150] = t102;
    $[151] = t106;
    $[152] = t107;
  } else {
    t107 = $[152];
  }
  const t108 = isFr ? "Est-ce que je peux utiliser l'extension sans compte Velyq ?" : "Can I use it without a Velyq account?";
  let t109;
  if ($[153] !== t108) {
    t109 = <AccordionTrigger className="text-left">{t108}</AccordionTrigger>;
    $[153] = t108;
    $[154] = t109;
  } else {
    t109 = $[154];
  }
  const t110 = isFr ? "Non \u2014 le clip a besoin d'un endroit o\xF9 atterrir. L'inscription est gratuite, prend 30 secondes, et d\xE9bloque tout l'\xE9cosyst\xE8me Velyq (tracker, ATS, lettres, pr\xE9paration d'entretien)." : "No \u2014 clips need somewhere to land. Signup is free, takes 30 seconds, and unlocks the whole Velyq ecosystem (tracker, ATS, letters, interview prep).";
  let t111;
  if ($[155] !== t110) {
    t111 = <AccordionContent>{t110}</AccordionContent>;
    $[155] = t110;
    $[156] = t111;
  } else {
    t111 = $[156];
  }
  let t112;
  if ($[157] !== t109 || $[158] !== t111) {
    t112 = <AccordionItem value="q3">{t109}{t111}</AccordionItem>;
    $[157] = t109;
    $[158] = t111;
    $[159] = t112;
  } else {
    t112 = $[159];
  }
  const t113 = isFr ? "Et sur Firefox ou Safari ?" : "What about Firefox or Safari?";
  let t114;
  if ($[160] !== t113) {
    t114 = <AccordionTrigger className="text-left">{t113}</AccordionTrigger>;
    $[160] = t113;
    $[161] = t114;
  } else {
    t114 = $[161];
  }
  const t115 = isFr ? "Chrome, Edge et Brave sont support\xE9s d\xE8s le lancement (m\xEAme moteur Chromium). Firefox arrive en Phase 2 si la traction est l\xE0. Safari n'est pas pr\xE9vu \xE0 court terme." : "Chrome, Edge and Brave are supported at launch (same Chromium engine). Firefox follows in Phase 2 if traction is there. Safari is not on the near-term roadmap.";
  let t116;
  if ($[162] !== t115) {
    t116 = <AccordionContent>{t115}</AccordionContent>;
    $[162] = t115;
    $[163] = t116;
  } else {
    t116 = $[163];
  }
  let t117;
  if ($[164] !== t114 || $[165] !== t116) {
    t117 = <AccordionItem value="q4">{t114}{t116}</AccordionItem>;
    $[164] = t114;
    $[165] = t116;
    $[166] = t117;
  } else {
    t117 = $[166];
  }
  const t118 = isFr ? "Comment je d\xE9sinstalle / r\xE9voque l'acc\xE8s ?" : "How do I uninstall / revoke access?";
  let t119;
  if ($[167] !== t118) {
    t119 = <AccordionTrigger className="text-left">{t118}</AccordionTrigger>;
    $[167] = t118;
    $[168] = t119;
  } else {
    t119 = $[168];
  }
  const t120 = isFr ? "Clic droit sur l'ic\xF4ne dans Chrome \u2192 \xAB Supprimer de Chrome \xBB. Ou depuis ton compte Velyq, onglet R\xE9glages \u2192 Extension \u2192 R\xE9voquer. Les tokens sont invalid\xE9s c\xF4t\xE9 serveur imm\xE9diatement." : "Right-click the Chrome icon \u2192 \"Remove from Chrome\". Or from Settings \u2192 Extension \u2192 Revoke in your Velyq account. Tokens are invalidated server-side immediately.";
  let t121;
  if ($[169] !== t120) {
    t121 = <AccordionContent>{t120}</AccordionContent>;
    $[169] = t120;
    $[170] = t121;
  } else {
    t121 = $[170];
  }
  let t122;
  if ($[171] !== t119 || $[172] !== t121) {
    t122 = <AccordionItem value="q5">{t119}{t121}</AccordionItem>;
    $[171] = t119;
    $[172] = t121;
    $[173] = t122;
  } else {
    t122 = $[173];
  }
  let t123;
  if ($[174] !== t100 || $[175] !== t107 || $[176] !== t112 || $[177] !== t117 || $[178] !== t122) {
    t123 = <Accordion type="single" collapsible={true} className="w-full">{t100}{t107}{t112}{t117}{t122}</Accordion>;
    $[174] = t100;
    $[175] = t107;
    $[176] = t112;
    $[177] = t117;
    $[178] = t122;
    $[179] = t123;
  } else {
    t123 = $[179];
  }
  let t124;
  if ($[180] !== t123 || $[181] !== t95) {
    t124 = <section className="container mx-auto px-4 py-16"><div className="mx-auto max-w-3xl">{t95}{t123}</div></section>;
    $[180] = t123;
    $[181] = t95;
    $[182] = t124;
  } else {
    t124 = $[182];
  }
  const t125 = isFr ? "Tes candidatures sans friction. Gratuit \xE0 installer." : "Frictionless applications. Free to install.";
  let t126;
  if ($[183] !== t125) {
    t126 = <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">{t125}</h2>;
    $[183] = t125;
    $[184] = t126;
  } else {
    t126 = $[184];
  }
  const t127 = isFr ? "Rejoins les chercheurs actifs qui laissent Velyq g\xE9rer la paperasse pendant qu'ils se concentrent sur les bonnes offres." : "Join active candidates letting Velyq handle the paperwork so they can focus on the right roles.";
  let t128;
  if ($[185] !== t127) {
    t128 = <p className="mt-3 text-muted-foreground">{t127}</p>;
    $[185] = t127;
    $[186] = t128;
  } else {
    t128 = $[186];
  }
  let t129;
  if ($[187] !== isFr) {
    t129 = EXTENSION_PUBLISHED ? <a href={CHROME_STORE_URL ?? "#"} target="_blank" rel="noreferrer" className="mt-6 inline-flex"><Button size="lg" className="gap-2 px-7"><Zap className="h-4 w-4" />{isFr ? "Ajouter \xE0 Chrome" : "Add to Chrome"}<ArrowRight className="h-4 w-4" /></Button></a> : <Link href={WAITLIST_HREF} className="mt-6 inline-flex"><Button size="lg" className="gap-2 px-7"><BellRing className="h-4 w-4" />{isFr ? "\xCAtre pr\xE9venu au lancement" : "Get notified at launch"}<ArrowRight className="h-4 w-4" /></Button></Link>;
    $[187] = isFr;
    $[188] = t129;
  } else {
    t129 = $[188];
  }
  let t130;
  if ($[189] !== t126 || $[190] !== t128 || $[191] !== t129) {
    t130 = <section className="container mx-auto px-4 pb-20 pt-8"><div className="mx-auto max-w-3xl rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5 p-10 text-center">{t126}{t128}{t129}</div></section>;
    $[189] = t126;
    $[190] = t128;
    $[191] = t129;
    $[192] = t130;
  } else {
    t130 = $[192];
  }
  let t131;
  if ($[193] !== t124 || $[194] !== t130 || $[195] !== t29 || $[196] !== t48 || $[197] !== t84 || $[198] !== t93) {
    t131 = <main>{t29}{t48}{t84}{t93}{t124}{t130}</main>;
    $[193] = t124;
    $[194] = t130;
    $[195] = t29;
    $[196] = t48;
    $[197] = t84;
    $[198] = t93;
    $[199] = t131;
  } else {
    t131 = $[199];
  }
  let t132;
  if ($[200] === Symbol.for("react.memo_cache_sentinel")) {
    t132 = <Footer />;
    $[200] = t132;
  } else {
    t132 = $[200];
  }
  let t133;
  if ($[201] !== t0 || $[202] !== t131 || $[203] !== t3) {
    t133 = <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">{t0}{t3}{t131}{t132}</div>;
    $[201] = t0;
    $[202] = t131;
    $[203] = t3;
    $[204] = t133;
  } else {
    t133 = $[204];
  }
  return t133;
}
function _temp(site) {
  return <span key={site} className="text-sm font-medium text-foreground/80">{site}</span>;
}
function StepCard(t0) {
  const $ = _c(15);
  const {
    index,
    icon,
    title,
    body
  } = t0;
  const t1 = String(index);
  let t2;
  if ($[0] !== t1) {
    t2 = t1.padStart(2, "0");
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = <div className="absolute -top-3 left-6 rounded-full border bg-background px-2 py-0.5 font-display text-[11px] font-bold text-primary">{t2}</div>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== icon) {
    t4 = <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">{icon}</div>;
    $[4] = icon;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== title) {
    t5 = <h3 className="font-display text-lg font-bold">{title}</h3>;
    $[6] = title;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== body) {
    t6 = <p className="mt-2 text-sm text-muted-foreground">{body}</p>;
    $[8] = body;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
    t7 = <div className="relative rounded-xl border bg-background/60 p-6 backdrop-blur">{t3}{t4}{t5}{t6}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
function FeatureCard(t0) {
  const $ = _c(17);
  const {
    icon,
    title,
    body,
    badge
  } = t0;
  const isPro = badge === "Pro";
  let t1;
  if ($[0] !== icon) {
    t1 = <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">{icon}</div>;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = isPro ? "default" : "outline";
  const t3 = isPro ? "bg-primary/90" : "";
  let t4;
  if ($[2] !== badge || $[3] !== t2 || $[4] !== t3) {
    t4 = <Badge variant={t2} className={t3}>{badge}</Badge>;
    $[2] = badge;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t1 || $[7] !== t4) {
    t5 = <div className="mb-3 flex items-center justify-between">{t1}{t4}</div>;
    $[6] = t1;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== title) {
    t6 = <h3 className="font-display text-base font-bold">{title}</h3>;
    $[9] = title;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== body) {
    t7 = <p className="mt-1.5 text-sm text-muted-foreground">{body}</p>;
    $[11] = body;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t5 || $[14] !== t6 || $[15] !== t7) {
    t8 = <div className="rounded-xl border bg-background/80 p-5">{t5}{t6}{t7}</div>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}
function PlanCard(t0) {
  const $ = _c(26);
  const {
    tier,
    tagline,
    features,
    ctaLabel,
    ctaHref,
    highlight
  } = t0;
  let t1;
  if ($[0] !== ctaHref) {
    t1 = ctaHref.startsWith("http");
    $[0] = ctaHref;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isExternal = t1;
  const t2 = `relative rounded-2xl border p-6 ${highlight ? "border-primary/60 bg-primary/[0.06]" : "bg-background/70"}`;
  let t3;
  if ($[2] !== tier) {
    t3 = <h3 className="font-display text-xl font-bold">{tier}</h3>;
    $[2] = tier;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== highlight) {
    t4 = highlight && <Badge className="bg-primary/90">Pour les candidats actifs</Badge>;
    $[4] = highlight;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <div className="flex items-baseline justify-between">{t3}{t4}</div>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== tagline) {
    t6 = <p className="mt-1 text-sm text-muted-foreground">{tagline}</p>;
    $[9] = tagline;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== features) {
    t7 = features.map(_temp2);
    $[11] = features;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = <ul className="mt-5 space-y-2">{t7}</ul>;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== ctaHref || $[16] !== ctaLabel || $[17] !== highlight || $[18] !== isExternal) {
    t9 = <div className="mt-6">{isExternal ? <a href={ctaHref} target="_blank" rel="noreferrer"><Button variant={highlight ? "default" : "outline"} className="w-full">{ctaLabel}</Button></a> : <Link href={ctaHref}><Button variant={highlight ? "default" : "outline"} className="w-full">{ctaLabel}</Button></Link>}</div>;
    $[15] = ctaHref;
    $[16] = ctaLabel;
    $[17] = highlight;
    $[18] = isExternal;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t2 || $[21] !== t5 || $[22] !== t6 || $[23] !== t8 || $[24] !== t9) {
    t10 = <div className={t2}>{t5}{t6}{t8}{t9}</div>;
    $[20] = t2;
    $[21] = t5;
    $[22] = t6;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
}
function _temp2(f) {
  return <li key={f} className="flex items-start gap-2 text-sm"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" /><span>{f}</span></li>;
}